import { GridColumn as Column } from "@progress/kendo-react-grid/dist/npm/GridColumn";
import * as React from "react";
import { useContext } from "react";
import { GridCell } from "@progress/kendo-react-grid/dist/npm/cells/GridCell";
import { EditProjectContext } from "../../../EditProjectContext";
import { ProjectState } from "../../../../ViewModel/ProjectState";
import { generateOdcColumns } from "../GenerateOdcColumns";
import { EngineeredSFDropdownCell } from "../InboundPipeline/EngineeredSFDropdownCell";
import { ColumnMenu } from "../../../../../Layout/Grid/Filter/ColumnMenu";
import { roundToTwoDecimals } from "../../../../../Grid/GridConstants";
import { NumericalKey } from "../../../../../Utils/utilityTypes";
import { PdsrClientViewModel } from "../../../../ViewModel/PdsrClientViewModel";
import { isArrayUndefinedOrEmpty } from "../../../../../Utils/arrays";
import { KendoReactDataGridFooterSumCell } from "../../../../../Layout/Grid/Cell/KendoReactDataGridFooterSumCell";
import { KendoReactColoredFooterCell } from "../../../../../Layout/Grid/Cell/KendoReactColoredFooterCell";
import { ReadOnlyProps } from "../../../ReadOnlyProps";

type Props = {
  odcGsdbsForColumns: Array<string> | undefined;
  ftlRouteNumber: string | undefined;
  pdsrsOnThisRoute: Array<PdsrClientViewModel>;
};

export default function ReturnPipelineGridColumns({
  odcGsdbsForColumns,
  ftlRouteNumber,
  pdsrsOnThisRoute,
  isReadOnly,
}: Props & ReadOnlyProps) {
  const { project } = useContext(EditProjectContext);
  const editable = project.State === ProjectState.Sandbox && !isReadOnly;

  const kendoReactFooterSumCell = (fieldKey: NumericalKey<PdsrClientViewModel>) => {
    return !isArrayUndefinedOrEmpty(pdsrsOnThisRoute) ? (
      <KendoReactDataGridFooterSumCell items={pdsrsOnThisRoute || []} fieldKey={fieldKey} />
    ) : (
      <td />
    );
  };

  const kendoReactColoredFooterCell = (cellText?: string) => {
    return !isArrayUndefinedOrEmpty(pdsrsOnThisRoute) ? (
      <KendoReactColoredFooterCell cellText={cellText} />
    ) : (
      <td />
    );
  };

  const defaultColumns = [
    <Column
      key="ContainerNetwork"
      field="ContainerNetwork"
      title="Container Network"
      filter="text"
      columnMenu={ColumnMenu}
      editable={false}
      resizable
      footerCell={() => kendoReactColoredFooterCell("Total")}
    />,
    <Column
      key="PlantGsdb"
      field="PlantGsdb"
      title="Plant"
      filter="text"
      columnMenu={ColumnMenu}
      editable={false}
      resizable
      footerCell={() => kendoReactColoredFooterCell()}
    />,
    <Column
      key="DockCode"
      field="DockCode"
      title="Dock"
      filter="text"
      columnMenu={ColumnMenu}
      editable={false}
      resizable
      footerCell={() => kendoReactColoredFooterCell()}
    />,
    <Column
      key="SupplierGsdb"
      field="SupplierGsdb"
      title="Supplier"
      filter="text"
      columnMenu={ColumnMenu}
      editable={false}
      resizable
      footerCell={() => kendoReactColoredFooterCell()}
    />,
    <Column
      key="MegaLdm"
      field="MegaLdm"
      title="MLDM"
      filter="numeric"
      columnMenu={ColumnMenu}
      editable={false}
      resizable
      format={roundToTwoDecimals}
      footerCell={() => kendoReactFooterSumCell("MegaLdm")}
    />,
    <Column
      key="EuroLdm"
      field="EuroLdm"
      title="ELDM"
      filter="numeric"
      columnMenu={ColumnMenu}
      editable={false}
      resizable
      format={roundToTwoDecimals}
      footerCell={() => kendoReactFooterSumCell("EuroLdm")}
    />,
    <Column
      key="Weight"
      field="Weight"
      title="KG"
      filter="numeric"
      columnMenu={ColumnMenu}
      editable={false}
      resizable
      format={roundToTwoDecimals}
      footerCell={() => kendoReactFooterSumCell("Weight")}
    />,
    <Column
      key="CubicMeters"
      field="CubicMeters"
      title="M3"
      filter="numeric"
      columnMenu={ColumnMenu}
      editable={false}
      resizable
      format={roundToTwoDecimals}
      footerCell={() => kendoReactFooterSumCell("CubicMeters")}
    />,
    <Column
      key="ShipFrequency"
      field="ShipFrequency"
      title="Engineered SF"
      filter="numeric"
      columnMenu={ColumnMenu}
      editable={editable}
      cell={editable ? EngineeredSFDropdownCell : GridCell}
      resizable
      footerCell={() => kendoReactColoredFooterCell()}
    />,
  ];

  return defaultColumns.concat(
    generateOdcColumns({
      odcs: odcGsdbsForColumns,
      ftlRouteNumber,
      clientViewModels: pdsrsOnThisRoute,
      disableColumn: isReadOnly,
    })
  );
}
